import { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import apiClient from "../services/apiClient";
import { message } from "antd";
import { ThemeContext } from "../context/ThemeContext";
import "./ModernLogin.css";
import { usePolicy } from "../context/PolicyContext";
import { useUser } from "../context/UserContext";
import * as roleUtils from "../utils/roleUtils";

const VerifyOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updatePolicies } = usePolicy();
  const { forceRefreshUserData, applyAuthResponse } = useUser();
  const { isDark, toggleTheme } = useContext(ThemeContext);
  
  const [isLoading, setIsLoading] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [email, setEmail] = useState("");
  const [fromSignup, setFromSignup] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);

  const baseUrl = process.env.REACT_APP_AUTH_URL;

  useEffect(() => {
    // Get email from location state or redirect back to appropriate page
    if (location.state && location.state.email) {
      setEmail(location.state.email);
      setFromSignup(location.state.fromSignup || false);
    } else {
      message.error("Invalid access. Please signup or login again.");
      navigate("/login");
    }
  }, [location.state, navigate, isDark]);

  useEffect(() => {
    // Countdown timer for resend cooldown
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Only allow digits
    if (value.length <= 6) {
      setOtpCode(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (otpCode.length !== 6) {
      message.error("Please enter a 6-digit verification code");
      return;
    }

    setIsLoading(true);

    try {
      const verifyData = {
        email: email,
        otpCode: otpCode,
      };

      console.log("Attempting OTP verification with:", verifyData);
      const response = await apiClient.post(`${baseUrl}/user/verify-email`, verifyData);
      console.log("OTP Verification Response:", response.data);

      if (!response.data.success) {
        throw new Error("OTP verification failed");
      }

      // Backend sets cookies; hydrate context immediately, then fetch full profile
      const authResponse = response.data.data.authResponse;
      updatePolicies(authResponse.policies);
      applyAuthResponse(authResponse);
      await forceRefreshUserData();

      const successMessage = fromSignup 
        ? "Registration completed successfully! Welcome to SimplyFI!" 
        : "Email verified successfully! Welcome back!";
        
      message.success(successMessage);

      setIsLoading(false);

      navigate(roleUtils.getPostLoginPath(authResponse.roles || []));

    } catch (error) {
      console.error("OTP Verification Error:", error);
      setIsLoading(false);

      let errorMessage = "Invalid verification code. Please try again.";

      if (error.response) {
        // Handle invalid OTP error (400 Bad Request)
        if (error.response.status === 400 && error.response.data.error) {
          message.error(error.response.data.error);
          return;
        }
        
        // Handle other validation errors
        if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        errorMessage = "Network error. Please check your connection.";
      }

      message.error(errorMessage);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;

    setIsResending(true);

    try {
      const resendData = { email: email };
      
      console.log("Resending OTP for:", email);
      const response = await apiClient.post(`${baseUrl}/user/resend-verification-otp`, resendData);
      
      if (response.data.success) {
        message.success("Verification code sent to your email!");
        setResendCooldown(60); // 60 second cooldown
      }
    } catch (error) {
      console.error("Resend OTP Error:", error);
      
      let errorMessage = "Failed to resend verification code. Please try again.";
      if (error.response && error.response.data) {
        errorMessage = error.response.data.message || error.response.data.error || errorMessage;
      }

      message.error(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className={`modern-login-container ${isDark ? "dark" : ""}`}>
      <div className="login-content">
        {/* Centered Form Section */}
        <div className="login-form-section">
          <div className="login-card">
            {/* Theme Toggle */}
            <button
              className="theme-toggle-btn"
              onClick={toggleTheme}
              title={`Switch to ${isDark ? "light" : "dark"} mode`}
            >
              {isDark ? "☀️" : "🌙"}
            </button>

            {/* Header */}
            <div className="login-header">
              <h2 className="welcome-title">
                {fromSignup ? "Complete Your Registration" : "Verify Your Email"}
              </h2>
              <p className="login-subtitle">
                {fromSignup 
                  ? `We've sent a 6-digit verification code to ${email} to complete your registration.`
                  : `We've sent a 6-digit verification code to ${email} to verify your account.`
                }
              </p>
            </div>

            {/* Form */}
            <form className="login-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <div className="input-container">
                  <div className="input-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <input
                    type="text"
                    name="otpCode"
                    value={otpCode}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    required
                    autoComplete="off"
                    style={{
                      textAlign: 'center',
                      fontSize: '1.2rem',
                      letterSpacing: '0.3rem',
                      fontFamily: 'monospace'
                    }}
                  />
                </div>
              </div>

              <button type="submit" className="submit-btn" disabled={isLoading || otpCode.length !== 6}>
                {isLoading ? "Verifying..." : (fromSignup ? "Complete Registration" : "Verify Email")}
              </button>
            </form>

            {/* Divider */}
            <div className="divider">
              <span>or</span>
            </div>

            {/* Resend Section */}
            <div className="social-login" style={{ flexDirection: 'column', gap: '1rem' }}>
              <button
                type="button"
                onClick={handleResendOTP}
                className={`submit-btn resend-btn ${resendCooldown > 0 ? 'disabled' : ''} ${isDark ? 'dark' : ''}`}
                disabled={isResending || resendCooldown > 0}
              >
                {isResending ? (
                  "Sending..."
                ) : resendCooldown > 0 ? (
                  `Resend in ${resendCooldown}s`
                ) : (
                  "Resend Verification Code"
                )}
              </button>

              <div className={`otp-info-text ${isDark ? 'dark' : ''}`}>
                <p>Code expires in 10 minutes</p>
                <p>Check your spam folder if you don't see the email</p>
              </div>
            </div>

            {/* Toggle Form */}
            <div className="toggle-form">
              <span className="toggle-text">
                {fromSignup ? (
                  <>Need to change email? <strong onClick={() => navigate("/signup-modern")} style={{ cursor: 'pointer' }}>Back to Signup</strong></>
                ) : (
                  <>Need to login instead? <strong onClick={() => navigate("/login")} style={{ cursor: 'pointer' }}>Back to Login</strong></>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyOTP;